import {
  type ClientInfo,
  type InteractionConfig,
  type RequestType,
} from '../../types';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { useTranslation } from '../../i18n/i18n';
import SidebarSkeleton from './SidebarSkeleton';
import StepsCard from './StepsCard';
import RequestedDataCard from './RequestedDataCard';
import RequestedDataPlaceholder from './RequestedDataPlaceholder';
import ClientLogo from '../ClientLogo';

type SidebarProps = {
  client?: ClientInfo;
  requestType?: RequestType;
  interactionConfig?: InteractionConfig;
  requestedDataGroups?: RequestedDataGroup[];
  requestedDataLoading?: boolean;
  loading?: boolean;
  variant?: 'mobile' | 'desktop';
};

/**
 * Sidebar — Desktop left panel showing branding and flow info.
 *
 * Figma: Left sidebar panel visible in all desktop journey frames.
 * Skeleton state: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=64-7730
 * Loaded state:   https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=339-22201
 */
export default function Sidebar({
  client,
  requestType,
  interactionConfig,
  requestedDataGroups,
  requestedDataLoading,
  loading,
  variant = 'desktop',
}: SidebarProps) {
  const { t } = useTranslation();
  const isIssuance = requestType === 'issuance';

  const strings: Record<
    RequestType,
    { title: string; description: string; descriptionFallback: string }
  > = {
    issuance: {
      title: t('sidebar.issuanceTitle'),
      description: t('sidebar.issuanceDescription', {
        clientName: client?.name ?? '',
      }),
      descriptionFallback: t('sidebar.issuanceDescriptionFallback'),
    },
    authentication: {
      title: t('sidebar.authenticationTitle'),
      description: t('sidebar.authenticationDescription', {
        clientName: client?.name ?? '',
      }),
      descriptionFallback: t('sidebar.authenticationDescriptionFallback'),
    },
    disclosure: {
      title: t('sidebar.title'),
      description: t('sidebar.description', { clientName: client?.name ?? '' }),
      descriptionFallback: t('sidebar.descriptionFallback'),
    },
    signature: {
      title: t('sidebar.title'),
      description: t('sidebar.description', { clientName: client?.name ?? '' }),
      descriptionFallback: t('sidebar.descriptionFallback'),
    },
  };
  const stringsForRequestType = strings[requestType ?? 'disclosure'];

  if (loading || (!client && !requestType)) {
    return <SidebarSkeleton />;
  }

  return (
    <div className="flex flex-col gap-5 w-full h-full">
      {/* Top section: logo + description */}
      <div className="flex flex-col gap-8 pt-10 md:pt-10">
        {/* Client logo (hidden on mobile, already shown in sheet header) */}
        <div className="hidden sidebar:flex items-center justify-start">
          <ClientLogo client={client} className="h-12 object-contain" />
        </div>

        {/* Title & description */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl md:text-2xl font-semibold text-sidebar-foreground leading-8">
            {stringsForRequestType.title}
          </h2>
          <p className="text-base text-muted-foreground leading-6">
            {client ? (
              <span>
                {stringsForRequestType.description
                  .split(client.name)
                  .map((part, i, arr) => (
                    <span key={i}>
                      {part}
                      {i < arr.length - 1 && <strong>{client.name}</strong>}
                    </span>
                  ))}
              </span>
            ) : (
              stringsForRequestType.descriptionFallback
            )}
          </p>
        </div>
      </div>

      {/* Content wrapper: steps + data + policy */}
      <div className="flex flex-1 flex-col gap-9 pb-10">
        <div className="flex flex-1 flex-col gap-9">
          <StepsCard clientName={client?.name} requestType={requestType} />

          {requestedDataGroups && requestedDataGroups.length > 0 ? (
            <RequestedDataCard
              groups={requestedDataGroups}
              isIssuance={isIssuance}
              variant={variant}
            />
          ) : variant !== 'mobile' ? (
            <RequestedDataPlaceholder
              loading={!!requestedDataLoading}
              isIssuance={isIssuance}
            />
          ) : null}

          {interactionConfig?.purposeStatement && (
            <div className="flex flex-col gap-4">
              <h3 className="text-base font-semibold text-sidebar-foreground leading-6">
                {t('sidebar.purposeStatementTitle')}
              </h3>
              <p className="text-sm text-muted-foreground leading-5">
                {interactionConfig.purposeStatement}
              </p>
            </div>
          )}

          {interactionConfig?.retentionPolicy && (
            <div className="flex flex-col gap-4">
              <h3 className="text-base font-semibold text-sidebar-foreground leading-6">
                {t('sidebar.retentionPolicyTitle')}
              </h3>
              <p className="text-sm text-muted-foreground leading-5">
                {interactionConfig.retentionPolicy}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
